import { ComponentProps, PropsWithChildren, forwardRef, useMemo, HTMLProps } from 'react';
import { Link } from 'react-router-dom';
import { css, keyframes, type Theme } from '@emotion/react';
import { lighten, transparentize } from 'polished';
import { Spinner } from '@phosphor-icons/react';
import { Tooltip, TooltipProps } from 'client/components/Tooltip';
import { Easing, Font, applyEffect, generateShadow, spinAnimation } from 'client/Theme';

export interface ButtonProps extends Omit<HTMLProps<HTMLButtonElement>, 'ref' | 'href'> {
  icon?: JSX.Element;
  href?: ComponentProps<typeof Link>['to'];
  external?: boolean;
  primary?: boolean;
  danger?: boolean;
  large?: boolean;
  small?: boolean;
  tiny?: boolean;
  loading?: boolean;
  selected?: boolean;
  pulsing?: boolean;
  highlight?: boolean;
  autoFocus?: boolean;
  tooltip?: string;
  tooltipSide?: TooltipProps['side'];
}

export const Button = forwardRef<HTMLButtonElement, PropsWithChildren<ButtonProps>>(
  (
    {
      icon,
      href = null,
      external = false,
      primary = false,
      danger = false,
      large,
      small,
      tiny,
      loading,
      disabled,
      selected = false,
      pulsing,
      highlight,
      style,
      className,
      autoFocus,
      tooltip = null,
      tooltipSide = 'bottom',
      onClick,
      children,
      ...other
    },
    ref
  ) => {
    const size = large ? 32 : small ? 16 : tiny ? 12 : 20;

    const iconElement = useMemo(() => {
      if (loading) return <Spinner size={size} css={spinIconStyle} />;
      if (icon) return <icon.type weight="fill" size={size} {...icon.props} />;

      return <Spinner size={0} css={spinIconStyle} />; // return a zero width spinner, so we can animate it when it appears/disappears
    }, [size, loading, icon]);

    let component = <button />;
    if (href) component = <a href={href as string} target="_blank" />;
    if (href && !external) component = <Link to={href} />;

    let button = (
      <component.type
        disabled={loading || disabled}
        aria-disabled={loading || disabled}
        tabIndex={loading || disabled ? -1 : undefined}
        css={[
          buttonStyle,
          primary && primaryButtonStyle,
          danger && dangerButtonStyle,
          large && largeButtonStyle,
          small && smallButtonStyle,
          tiny && tinyButtonStyle,
          selected && selectedButtonStyle,
          href && linkStyle,
          pulsing && pulseStyle,
          highlight && hightlightStyle,
        ]}
        style={style}
        autoFocus={autoFocus}
        onClick={onClick}
        ref={ref}
        className={`button ${disabled ? 'disabled' : ''} ${className || ''}`}
        {...component.props}
        {...(selected ? { 'data-selected': true } : {})}
        {...(tooltip ? { 'aria-label': tooltip } : {})}
        {...(other as any)}
      >
        <span>{children}</span>
        {iconElement && <span className="icon">{iconElement}</span>}
      </component.type>
    );

    if (tooltip) {
      button = (
        <Tooltip title={tooltip} side={tooltipSide}>
          {button}
        </Tooltip>
      );
    }

    return button;
  }
);

export const buttonStyle = (theme: Theme) => css`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 0;
  min-width: 46px;
  min-height: 46px;
  font-family: ${Font.header};
  font-weight: 500;
  font-size: 0.875rem;
  line-height: 1.75;
  text-transform: none;
  text-decoration: none;
  background-color: ${theme.color.grey[60]};
  border-radius: 128px;
  border: none;
  color: ${theme.color.foreground};
  user-select: none;
  cursor: pointer;

  box-shadow: none;

  transition:
    box-shadow 200ms ${Easing.basic},
    background-color 200ms ${Easing.basic},
    transform 200ms ${Easing.basic};

  &:hover {
    background-color: ${applyEffect(theme.color.grey[60], 'hover')};
    box-shadow: ${generateShadow({ primary: theme.color.black, scale: 1.5 })};
  }

  &:disabled,
  &.disabled {
    background-color: ${transparentize(0.5, theme.color.grey[60])};
    color: ${theme.color.grey[80]};
    pointer-events: none;
    cursor: not-allowed;
  }

  > span {
    margin-bottom: -2px;
    margin-left: 1.3rem;
    margin-right: 1.3rem;

    & + .icon {
      margin-left: -0.6rem;
    }

    &:empty {
      display: none;

      & + .icon {
        margin-top: -4px;
        margin-left: 0;
        margin-right: 0;
      }
    }
  }

  .icon {
    display: flex;
    margin-top: -2px;
    margin-left: 0;
    margin-right: 10px;
    opacity: 0.8;
  }
`;

export const primaryButtonStyle = (theme: Theme) => css`
  background-color: ${theme.color.primary[100]};
  color: ${theme.color.white};

  &:hover {
    background-color: ${applyEffect(theme.color.primary[100], 'hover')};
  }

  &:disabled,
  &.disabled {
    .icon {
      color: ${transparentize(0.75, theme.color.white)};
    }
  }

  .icon {
    color: ${theme.color.white};
    transition: color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
  }
`;

export const dangerButtonStyle = (theme: Theme) => css`
  background-color: ${theme.color.red[60]};
  color: ${theme.color.red.text};

  &:hover {
    background-color: ${applyEffect(theme.color.red[100], 'hover')};
    color: ${theme.color.white};

    .icon {
      color: ${theme.color.white};
    }
  }

  &:disabled,
  &.disabled {
    .icon {
      color: ${transparentize(0.75, theme.color.red.text)};
    }
  }

  .icon {
    color: ${theme.color.red.text};
    transition: color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
  }
`;

export const spinIconStyle = (theme: Theme) => css`
  color: ${theme.color.grey[100]};
  animation: ${spinAnimation} 2s linear infinite;
  transition: all 0.1s ease-out;
`;

const largeButtonStyle = css`
  padding: 1rem 0;
  font-size: 1rem;

  > span {
    margin-bottom: -4px;
    margin-left: 2rem;
    margin-right: 2rem;

    & + .icon {
      margin-left: -1rem;
    }

    &:empty {
      display: none;

      & + .icon {
        margin-left: 0;
        margin-right: 0;
      }
    }
  }

  .icon {
    margin-top: -4px;
    margin-left: 0;
    margin-right: 1rem;
  }
`;

const smallButtonStyle = css`
  padding: 0.3rem 0;
  min-width: 29px;
  min-height: 29px;
  font-size: 0.8rem;

  > span {
    margin-left: 1rem;
    margin-right: 1rem;

    & + .icon {
      margin-left: -0.6rem;
      margin-right: 0.5rem;
    }

    &:empty {
      display: none;

      & + .icon {
        margin-left: 0;
        margin-right: 0;
      }
    }
  }

  .icon {
    margin-top: -4px;
    margin-left: 0;
    margin-right: 1rem;
  }
`;

const tinyButtonStyle = css`
  padding: 0;
  min-width: 0;
  height: 24px;
  min-height: 0;
  font-size: 12px;

  > span {
    margin-left: 10px;
    margin-right: 10px;

    & + .icon {
      margin-left: -0.4rem;
      margin-right: 0.5rem;
    }

    &:empty {
      display: none;

      & + .icon {
        margin-left: 0;
        margin-right: 0;
      }
    }
  }
`;

const selectedButtonStyle = (theme: Theme) => css`
  background-color: ${theme.color.primary[60]};
  color: ${theme.color.primary.text};

  &:hover {
    background-color: ${applyEffect(theme.color.primary[60], 'hover')};
  }

  .icon {
    color: ${theme.color.primary.text};
    transition: color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
  }
`;

const linkStyle = css`
  display: flex;
  text-decoration: none;
`;

const pulseAnimation = (theme: Theme) => keyframes`
  0% { --background-color: ${theme.color.grey[60]}; }
  50% { --background-color: ${lighten(0.05, theme.color.grey[60])}; }
  100% { --background-color: ${theme.color.grey[60]}; }
`;

const pulseStyle = (theme: Theme) => css`
  @property --background-color {
    syntax: '<color>';
    inherits: false;
    initial-value: ${theme.color.primary[100]};
  }

  background-color: var(--background-color);

  animation: ${pulseAnimation(theme)} 1s infinite;
`;

const hightlightAnimation = (theme: Theme) => keyframes`
  0% { padding: 0; translate: 0 0; }
  100% { padding: 16px; translate: -16px -16px;  opacity: 0; }
`;

const hightlightStyle = (theme: Theme) => css`
  > * {
    z-index: 1;
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(transparent 0%, ${theme.color.primary[100]} 50%, ${theme.color.primary[100]});
    border-radius: 128px;
    z-index: 1;
    box-sizing: content-box;

    animation: ${hightlightAnimation(theme)} 1s cubic-bezier(0.22, 1, 0.36, 1);
  }

  &:hover {
    &::before {
      background: radial-gradient(
        transparent 0%,
        ${applyEffect(theme.color.primary[100], 'hover')} 50%,
        ${applyEffect(theme.color.primary[100], 'hover')}
      );
    }
  }
`;
